import { FC, useEffect, useState } from "react";

import { Alert, Button, Stack } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  deleteGeoDataFileProcessingRun,
  GeoDataFileProcessingRunModel,
  getGeoDataFileProcessingRun,
  getOrg,
  listRegion,
  listSubregion,
  RegionWithSubregions,
  SubregionModel,
} from "@rubi/api-generated";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  Crumb,
  DevPage,
  ErrorMsg,
  JsonPre,
  JsonValue,
  Section,
} from "./common";
import {
  onRowActivateKeyDown,
  RegionBreadcrumbs,
  RegionGrid,
} from "./regionShared";

const POLL_INTERVAL_MS = 2000;
const RUNNING_STATUSES = new Set(["pending", "processing"]);

const subregionColumns: GridColDef<SubregionModel>[] = [
  { field: "id", headerName: "ID", flex: 1, minWidth: 280 },
  {
    field: "metadata",
    headerName: "Metadata",
    flex: 2,
    minWidth: 300,
    valueGetter: (value: SubregionModel["metadata"]) => JSON.stringify(value),
  },
];

export const DevGeoDataFileProcessingRunPage: FC = () => {
  const { orgId = "", fileId = "", runId = "" } = useParams();
  const navigate = useNavigate();

  const [run, setRun] = useState<GeoDataFileProcessingRunModel | null>(null);
  const [subregions, setSubregions] = useState<SubregionModel[]>([]);
  const [regions, setRegions] = useState<RegionWithSubregions[]>([]);
  const [activeRegionId, setActiveRegionId] = useState<string | null>(null);
  const [regionsLoading, setRegionsLoading] = useState(true);
  const [regionsError, setRegionsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const response = await getGeoDataFileProcessingRun({
          path: { org_id: orgId, run_id: runId },
        });
        if (cancelled) return;
        const data = response.data ?? null;
        setRun(data);
        if (data && !RUNNING_STATUSES.has(data.status)) {
          clearInterval(interval);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load run");
          clearInterval(interval);
        }
      }
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [orgId, runId]);

  const openSubregion = (subregionId: string) =>
    navigate(`/dev_admin/region/${orgId}/subregion/${subregionId}`);

  useEffect(() => {
    if (run?.status !== "processed") return;
    listSubregion({
      path: { org_id: orgId },
      query: { geo_data_file_processing_run_id: runId },
    }).then((response) => setSubregions(response.data?.subregions ?? []));
  }, [orgId, runId, run?.status]);

  useEffect(() => {
    if (run?.status !== "processed") return;
    let stale = false;
    setRegionsLoading(true);
    Promise.all([
      listRegion({
        path: { org_id: orgId },
        // The live API filters by this param but the generated client doesn't
        // know it yet (types `query` as `never`). Drop the cast after the
        // next `yarn openapi-codegen` picks it up.
        query: { geo_data_file_processing_run_id: runId } as never,
      }),
      getOrg({ path: { org_id: orgId } }),
    ]).then(
      ([regionsResponse, orgResponse]) => {
        if (stale) return;
        setRegions(regionsResponse.data?.regions ?? []);
        setActiveRegionId(orgResponse.data?.region_id ?? null);
        setRegionsError(null);
        setRegionsLoading(false);
      },
      (e) => {
        if (stale) return;
        setRegionsError(
          e instanceof Error ? e.message : "Failed to load Regions",
        );
        setRegionsLoading(false);
      },
    );
    return () => {
      stale = true;
    };
  }, [orgId, runId, run?.status]);

  const handleDelete = async () => {
    if (!window.confirm(`Delete run ${runId}? This cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteGeoDataFileProcessingRun({
        path: { org_id: orgId, run_id: runId },
      });
      navigate(`/dev_admin/region/${orgId}/geo_data_file/${fileId}`);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : "Failed to delete");
      setDeleting(false);
    }
  };

  return (
    <DevPage
      title={`Run ${runId}`}
      breadcrumbs={
        <RegionBreadcrumbs orgId={orgId}>
          <Crumb>
            <Link to={`/dev_admin/region/${orgId}/geo_data_file/${fileId}`}>
              {fileId}
            </Link>
          </Crumb>
          <Crumb>{runId}</Crumb>
        </RegionBreadcrumbs>
      }
    >
      <Section subtitle="Status">
        <ErrorMsg msg={error} />
        <table className="dev-table dev-table--details">
          <tbody>
            <tr>
              <td>status</td>
              <td>
                {run?.status}
                {RUNNING_STATUSES.has(run?.status ?? "") && " (polling…)"}
              </td>
            </tr>
            <tr>
              <td>options</td>
              <td>
                <JsonPre
                  object={(run?.options as unknown as JsonValue) ?? null}
                />
              </td>
            </tr>
            {run?.failure_reason && (
              <tr>
                <td>failure_reason</td>
                <td>
                  <Alert severity="error">{run.failure_reason}</Alert>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {deleteError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {deleteError}
          </Alert>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting…" : "Delete run"}
          </Button>
        </Stack>
      </Section>

      {run?.status === "processed" && (
        <Section subtitle="Subregions produced">
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Button
              variant="contained"
              disabled={subregions.length === 0}
              onClick={() =>
                navigate(`/dev_admin/region/${orgId}/region/new?runId=${runId}`)
              }
            >
              Create Region from this run
            </Button>
          </Stack>
          <div style={{ width: "100%" }}>
            <DataGrid
              rows={subregions}
              columns={subregionColumns}
              autoHeight
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              pageSizeOptions={[10, 25, 50]}
              onRowClick={(params) => openSubregion(String(params.id))}
              onCellKeyDown={onRowActivateKeyDown(openSubregion)}
              sx={{ cursor: "pointer" }}
            />
          </div>
        </Section>
      )}

      {run?.status === "processed" && (
        <Section subtitle="Regions produced">
          <ErrorMsg msg={regionsError} />
          <RegionGrid
            orgId={orgId}
            regions={regions}
            activeRegionId={activeRegionId}
            loading={regionsLoading}
          />
        </Section>
      )}
    </DevPage>
  );
};
